<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { CustomerService } from '@/services/customer-service'
import { Log } from '@/modules/util/log'
import { debounce } from '@/modules/util/debounce'

const customerService = new CustomerService
const props = defineProps(['field', 'modelValue'])
const emit = defineEmits(['update:modelValue'])

const vendors = ref([])
const selected = ref(null)
const search = ref('')

const displayVendors = computed(() => {
	return [
		...vendors.value.filter(v => String(v.customer_id) !== String(props.modelValue)),
		selected.value ? selected.value : undefined,
	]
})

const onSearch = (val) => {
	search.value = val
	listVendors(val)
}

const getVendor = async (id) => {
	if (vendors.value.find(v => String(v.customer_id) === String(id))) {
		selected.value = vendors.value.find(v => String(v.customer_id) === String(id))
		return
	}

	try {
		const { status, results } = (await customerService.get(id)).body

		if (!status) {
			throw new Error(JSON.stringify(results))
		}

		selected.value = results[0]
	} catch (err) {
		console.error('getVendor:', err.message)
		Log.warning('Could not fetch vendor details.')
	}
}

const listVendors = debounce(async (q) => {
	try {
		const { status, results } = (await customerService.list({ q })).body

		if (!status) {
			throw new Error(JSON.stringify(results))
		}

		vendors.value = results
	} catch (err) {
		console.error('listVendors:', err.message)
		Log.error('Could not refresh vendors.')
	}
}, 150)

const stop = watch(() => props.modelValue, (val) => {
	if (val) {
		getVendor(val)
	} else {
		selected.value = null
	}
}, { immediate: true })

const onUpdate = (val) => {
	stop()
	emit('update:modelValue', val)
	getVendor(val)
}
</script>

<template>
<v-autocomplete
	v-if="!(field.readonly || field.listitem)"
	:model-value="modelValue"
	:label="field.label"
	:items="displayVendors"
	:search="search"
	item-title="customer_name"
	item-value="customer_id"
	no-data-text="Search for a customer"
	hide-details="auto"
	@update:search="onSearch"
	@update:model-value="onUpdate"
/>
<v-list-item v-else-if="field.listitem">
	<v-list-item-subtitle>{{ field.label }}</v-list-item-subtitle>
	<v-list-item-title>{{ selected?.customer_name || '' }}</v-list-item-title>
</v-list-item>
<v-text-field
	v-else
	:model-value="selected?.customer_name || ''"
	:label="field.label"
	hide-details
	readonly
	variant="outlined"
	class="mt-2"
/>
</template>

<style lang="scss" scoped>

</style>